import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/PhpAuthContext';
import { useHospitalAuth } from '../../contexts/HospitalAuthContext';
import { useHospitalBranding } from '../../contexts/HospitalBrandingContext';
import { useData } from '../../contexts/DataContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from '../../components/ui/use-toast';
import { apiRequest } from '../../config/api';
import { 
  Users, 
  Settings, 
  BarChart3, 
  FileText,
  LogOut,
  Stethoscope,
  Calendar,
  ClipboardList,
  Clock,
  AlertCircle,
  ArrowRight
} from 'lucide-react';

import AdminOverviewTab from '../../components/admin/AdminOverviewTab';
import AdminDoctorsTab from '../../components/admin/AdminDoctorsTab';
import AdminPatientsTab from '../../components/admin/AdminPatientsTab';
import AppointmentsTab from '../../components/admin/AppointmentsTab';
import AdminSubmissionsTab from '../../components/admin/AdminSubmissionsTab';
import AdminSettingsTab from '../../components/admin/AdminSettingsTab';
import CampSubmissions from '../../components/doctor/CampSubmissions';
import Attendance from '../../components/doctor/Attendance';

const AdminDashboard = () => {
  const { profile, signOut } = useAuth();
  const { hospitalUser, hospital, hospitalSignOut, subscriptionStatus } = useHospitalAuth();
  const { branding, getHospitalName } = useHospitalBranding();
  const { submissions, doctors, patients, addDoctor, updateDoctor, fetchSubmissions, fetchDoctors, fetchPatients } = useData();
  const navigate = useNavigate();
  
  // Get admin name - prefer hospital user name, fallback to profile
  const adminName = hospitalUser?.name || profile?.full_name || profile?.name || 'Admin';
  const hospitalName = getHospitalName();
  
  // Get role from hospitalUser (CLINIC_ADMIN, CHIEF_DOCTOR) or profile (admin, chief_doctor, etc.)
  const userRole = hospitalUser?.role || profile?.role || '';
  
  // Map role to display name
  const getRoleDisplayName = (role) => {
    if (!role) return '';
    const roleUpperCheck = role.toUpperCase();
    if (roleUpperCheck === 'CLINIC_ADMIN') return 'Clinic Admin';
    if (roleUpperCheck === 'CHIEF_DOCTOR') return 'Chief Doctor';
    if (roleUpperCheck === 'ADMIN') return 'Admin';
    if (roleUpperCheck === 'DOCTOR') return 'Doctor';
    if (roleUpperCheck === 'STAFF') return 'Staff';
    if (roleUpperCheck === 'CLINIC_LOGIN') return 'Clinic Login';
    // Capitalize first letter of each word for other roles
    return role.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  };
  
  const roleDisplayName = getRoleDisplayName(userRole);
  
  // Check if user is a chief doctor (can toggle "View as Admin")
  // Check both uppercase and lowercase versions to handle both CHIEF_DOCTOR and chief_doctor
  // userRole is already from hospitalUser?.role || profile?.role, so this check covers both cases
  const currentRoleUpper = (userRole || '').toUpperCase();
  const currentRoleLower = (userRole || '').toLowerCase();
  const isChiefDoctor = currentRoleUpper === 'CHIEF_DOCTOR' || currentRoleLower === 'chief_doctor';
  const [viewAsAdmin, setViewAsAdmin] = useState(false);
  
  const [activeTab, setActiveTab] = useState('overview');
  const [campSubmissionsInitialTab, setCampSubmissionsInitialTab] = useState('form');
  const [attendanceInitialTab, setAttendanceInitialTab] = useState('checkin');
  const [attendanceKey, setAttendanceKey] = useState(0);
  const [appointmentsCount, setAppointmentsCount] = useState(0);
  const [scheduledAppointmentsCount, setScheduledAppointmentsCount] = useState(0);
  const [submissionsCount, setSubmissionsCount] = useState(0);
  const [campSubmissionsCount, setCampSubmissionsCount] = useState(0);
  const [attendanceCount, setAttendanceCount] = useState(0);
  const [leaveRequestsCount, setLeaveRequestsCount] = useState(0);
  const [leaveRequestsPendingCount, setLeaveRequestsPendingCount] = useState(0);
  const [leaveRequestsApprovedCount, setLeaveRequestsApprovedCount] = useState(0);
  const [appointments, setAppointments] = useState([]);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [trialInfo, setTrialInfo] = useState(null);
  const [planType, setPlanType] = useState(null);
  const [hasPendingEnrollment, setHasPendingEnrollment] = useState(false);
  const [pendingEnrollmentRequest, setPendingEnrollmentRequest] = useState(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  // Fetch hospital plan type and trial info
  useEffect(() => {
    const fetchHospitalPlanInfo = async () => {
      if (hospital?.id) {
        try {
          const data = await apiRequest(`get_hospital_by_slug.php?slug=${hospital.slug}`);
          if (data.success && data.hospital) {
            setPlanType(data.hospital.plan_type || 'TRIAL');
            
            // Set trial info if plan type is TRIAL
            if (data.hospital.plan_type === 'TRIAL' && data.hospital.trial_end_at && data.hospital.is_trial_active) {
              const trialEnd = new Date(data.hospital.trial_end_at);
              const now = new Date();
              const daysLeft = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
              
              setTrialInfo({
                endDate: trialEnd,
                daysLeft: daysLeft > 0 ? daysLeft : 0,
                isActive: data.hospital.is_trial_active
              });
            }
            
            // Set subscription info if plan is not TRIAL (SILVER, GOLD, etc.)
            if (data.hospital.plan_type && data.hospital.plan_type !== 'TRIAL') {
              // Get subscription details from get_hospital_status
              try {
                const statusData = await apiRequest(`get_hospital_status.php?hospital_id=${hospital.id}`);
                if (statusData.success && statusData.subscription) {
                  const subscription = statusData.subscription;
                  const expiryDate = subscription.end_at ? new Date(subscription.end_at) : null;
                  
                  // Get payment plan from enrollment requests
                  let paymentPlan = null;
                  try {
                    const enrollmentData = await apiRequest(`get_enrollment_requests.php?status=APPROVED&hospital_id=${hospital.id}`);
                    if (enrollmentData.success && enrollmentData.requests && enrollmentData.requests.length > 0) {
                      // Get the most recent approved enrollment request
                      const latestEnrollment = enrollmentData.requests[0];
                      paymentPlan = latestEnrollment.payment_plan || null;
                    }
                  } catch (err) {
                    console.error('Error fetching enrollment requests:', err);
                  }
                  
                  // Use plan_type from hospital (SILVER/GOLD), payment_plan from enrollment
                  setSubscriptionInfo({
                    planType: data.hospital.plan_type, // SILVER or GOLD
                    expiryDate: expiryDate,
                    paymentPlan: paymentPlan, // MONTHLY or YEARLY
                    isActive: subscription.active || false
                  });
                } else {
                  // Fallback: use hospital data if subscription not available
                  const expiryDate = data.hospital.subscription_end ? new Date(data.hospital.subscription_end) : 
                                   (data.hospital.trial_end_at ? new Date(data.hospital.trial_end_at) : null);
                  
                  // Try to get payment plan from enrollment requests
                  let paymentPlan = null;
                  try {
                    const enrollmentData = await apiRequest(`get_enrollment_requests.php?status=APPROVED&hospital_id=${hospital.id}`);
                    if (enrollmentData.success && enrollmentData.requests && enrollmentData.requests.length > 0) {
                      const latestEnrollment = enrollmentData.requests[0];
                      paymentPlan = latestEnrollment.payment_plan || null;
                    }
                  } catch (err) {
                    console.error('Error fetching enrollment requests:', err);
                  }
                  
                  setSubscriptionInfo({
                    planType: data.hospital.plan_type,
                    expiryDate: expiryDate,
                    paymentPlan: paymentPlan,
                    isActive: true
                  });
                }
              } catch (error) {
                console.error('Error fetching subscription info:', error);
                // Fallback: use hospital data
                const expiryDate = data.hospital.subscription_end ? new Date(data.hospital.subscription_end) : 
                                 (data.hospital.trial_end_at ? new Date(data.hospital.trial_end_at) : null);
                
                // Try to get payment plan from enrollment requests
                let paymentPlan = null;
                try {
                  const enrollmentData = await apiRequest(`get_enrollment_requests.php?status=APPROVED&hospital_id=${hospital.id}`);
                  if (enrollmentData.success && enrollmentData.requests && enrollmentData.requests.length > 0) {
                    const latestEnrollment = enrollmentData.requests[0];
                    paymentPlan = latestEnrollment.payment_plan || null;
                  }
                } catch (err) {
                  console.error('Error fetching enrollment requests:', err);
                }
                
                setSubscriptionInfo({
                  planType: data.hospital.plan_type,
                  expiryDate: expiryDate,
                  paymentPlan: paymentPlan,
                  isActive: true
                });
              }
            } else {
              setSubscriptionInfo(null);
            }
          }
        } catch (error) {
          console.error('Error fetching hospital plan info:', error);
        }
      }
    };
    
    fetchHospitalPlanInfo();
  }, [hospital]);

  // Check for pending enrollment requests
  useEffect(() => {
    const checkPendingEnrollment = async () => {
      if (hospital?.id) {
        try {
          const data = await apiRequest(`check_pending_enrollment.php?hospital_id=${hospital.id}`);
          if (data.success) {
            setHasPendingEnrollment(data.has_pending || false);
            setPendingEnrollmentRequest(data.enrollment_request || null);
          }
        } catch (error) {
          console.error('Error checking pending enrollment:', error);
          setHasPendingEnrollment(false);
          setPendingEnrollmentRequest(null);
        }
      }
    };
    
    checkPendingEnrollment();
    
    // Check every 30 seconds for updates
    const interval = setInterval(checkPendingEnrollment, 30000);
    return () => clearInterval(interval);
  }, [hospital?.id]);

  useEffect(() => {
    fetchSubmissions();
    fetchDoctors();
    // Only fetch patients if user has permission
    // Check both profile (regular auth) and hospitalUser (hospital auth)
    const currentUser = profile || hospitalUser;
    const userRole = (profile?.role || hospitalUser?.role || '').toUpperCase();
    const roleLower = (profile?.role || hospitalUser?.role || '').toLowerCase();
    
    // Allow admin, CLINIC_ADMIN, doctor, chief_doctor, CHIEF_DOCTOR, and clinic_login roles
    const allowedRoles = ['ADMIN', 'CLINIC_ADMIN', 'DOCTOR', 'CHIEF_DOCTOR', 'CLINIC_LOGIN'];
    const allowedRolesLower = ['admin', 'doctor', 'chief_doctor', 'clinic_login'];
    
    if (currentUser && (allowedRoles.includes(userRole) || allowedRolesLower.includes(roleLower))) {
      // When viewAsAdmin is true for chief_doctor, fetch all data like admin
      // Otherwise, fetch normally (tabs will handle filtering)
      if (viewAsAdmin && isChiefDoctor) {
        fetchPatients(null, true);
      } else {
        fetchPatients();
      }
      fetchAppointmentsCount();
      fetchSubmissionsCount();
      fetchCampSubmissionsCount();
      fetchAttendanceCount();
      fetchLeaveRequestsCount();
      fetchAppointments();
    }
  }, [fetchSubmissions, fetchDoctors, fetchPatients, profile, hospitalUser, viewAsAdmin, isChiefDoctor]);

  // Update current date/time every second (IST)
  useEffect(() => {
    const updateDateTime = () => {
      setCurrentDateTime(new Date());
    };
    
    updateDateTime(); // Initial update
    const timer = setInterval(updateDateTime, 1000);
    
    return () => clearInterval(timer);
  }, []);

  const fetchAppointmentsCount = async () => {
    try {
      const data = await apiRequest('get_appointments_count.php');
      if (data.success) {
        setAppointmentsCount(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching appointments count:', error);
      setAppointmentsCount(0);
    }
  };

  const fetchSubmissionsCount = async () => {
    try {
      const data = await apiRequest('get_submissions_count.php');
      if (data.success) {
        setSubmissionsCount(data.total || 0);
      } else {
        console.error('Failed to fetch submissions count:', data.error);
        setSubmissionsCount(0);
      }
    } catch (error) {
      console.error('Error fetching submissions count:', error);
      setSubmissionsCount(0);
    }
  };

  const fetchCampSubmissionsCount = async () => {
    try {
      const data = await apiRequest('get_camp_submissions.php?limit=1&offset=0');
      if (data.success) {
        setCampSubmissionsCount(data.total || 0);
      } else {
        console.error('Failed to fetch camp submissions count:', data.error);
        setCampSubmissionsCount(0);
      }
    } catch (error) {
      console.error('Error fetching camp submissions count:', error);
      setCampSubmissionsCount(0);
    }
  };

  const fetchAttendanceCount = async () => {
    try {
      const data = await apiRequest('get_attendance.php?limit=1&offset=0');
      if (data.success) {
        setAttendanceCount(data.total || 0);
      } else {
        console.error('Failed to fetch attendance count:', data.error);
        setAttendanceCount(0);
      }
    } catch (error) {
      console.error('Error fetching attendance count:', error);
      setAttendanceCount(0);
    }
  };

  const fetchLeaveRequestsCount = async () => {
    try {
      const data = await apiRequest('get_leave_requests.php?limit=1000&offset=0');
      if (data.success) {
        const leaveRequests = data.leave_requests || [];
        setLeaveRequestsCount(leaveRequests.length);
        // Case-insensitive status comparison
        const pending = leaveRequests.filter(lr => (lr.status || '').toLowerCase() === 'pending').length;
        const approved = leaveRequests.filter(lr => (lr.status || '').toLowerCase() === 'approved').length;
        setLeaveRequestsPendingCount(pending);
        setLeaveRequestsApprovedCount(approved);
      } else {
        console.error('Failed to fetch leave requests count:', data.error);
        setLeaveRequestsCount(0);
        setLeaveRequestsPendingCount(0);
        setLeaveRequestsApprovedCount(0);
      }
    } catch (error) {
      console.error('Error fetching leave requests count:', error);
      setLeaveRequestsCount(0);
      setLeaveRequestsPendingCount(0);
      setLeaveRequestsApprovedCount(0);
    }
  };

  const fetchAppointments = async () => {
    try {
      const data = await apiRequest('get_appointments_list.php');
      if (data.success) {
        const appointmentsList = data.appointments || [];
        setAppointments(appointmentsList);
      } else {
        console.error('Failed to fetch appointments:', data.error);
        setAppointments([]);
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
      setAppointments([]);
    }
  };

  // Recalculate scheduled appointments count whenever appointments change
  useEffect(() => {
    // Count scheduled appointments
    // Check for both 'Scheduled' (capital S) and 'scheduled' (lowercase) to handle different API responses
    const scheduled = appointments.filter(apt => {
      const status = (apt.status || '').toLowerCase();
      return status === 'scheduled';
    }).length;
    
    // Debug logging
    console.log('AdminDashboard - Total appointments:', appointments.length);
    console.log('AdminDashboard - Scheduled appointments count:', scheduled);
    if (appointments.length > 0) {
      console.log('AdminDashboard - Sample appointment statuses:', 
        appointments.slice(0, 3).map(apt => ({ id: apt.id, status: apt.status }))
      );
    }
    
    setScheduledAppointmentsCount(scheduled);
  }, [appointments]);

  // Refresh function to be passed to child components
  const refreshData = () => {
    fetchSubmissions();
    fetchSubmissionsCount();
  };

  const handleLogout = async () => {
    // Get hospital slug before clearing session
    const hospitalSlug = hospital?.slug || branding?.slug;
    
    // Clear both regular auth and hospital auth
    await signOut();
    await hospitalSignOut();
    
    // Redirect to hospital landing page if slug is available, otherwise to dental hub landing page
    if (hospitalSlug) {
      navigate(`/${hospitalSlug}`);
    } else {
      navigate('/');
    }
    
    toast({
      title: 'Logged out successfully',
      description: 'See you next time!'
    });
  };

  const getSubmissionStats = () => {
    const total = submissionsCount || submissions.length;
    const inProgress = submissions.filter(s => s.status === 'in-progress').length;
    const completed = submissions.filter(s => s.status === 'completed').length;
    
    // Count only active doctors (status === 'Active')
    const activeDoctorsCount = doctors.filter(doctor => {
      const status = doctor.status;
      // Only count doctors with status === 'Active'
      // If status is not available, check is_active as fallback
      if (status !== undefined && status !== null) {
        return status === 'Active';
      }
      // Fallback to is_active if status field doesn't exist
      return doctor.is_active !== false;
    }).length;
    
    const totalDoctorsCount = doctors.length;
    
    return { 
      total, 
      inProgress, 
      completed, 
      activeDoctors: activeDoctorsCount,
      activeDoctorsDisplay: `${activeDoctorsCount}(${totalDoctorsCount})`, // Format: "5(10)"
      totalDoctors: totalDoctorsCount,
      totalPatients: patients.length,
      totalAppointments: appointmentsCount,
      campSubmissions: campSubmissionsCount
    };
  };

  const stats = getSubmissionStats();

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'doctors', label: 'Doctors', icon: Users },
    { id: 'patients', label: 'Patients', icon: Stethoscope },
    { id: 'appointments', label: 'Appointments', icon: Calendar },
    { id: 'submissions', label: 'Appointment History', icon: FileText },
    { id: 'camp_submissions', label: 'Camp Submissions', icon: ClipboardList },
    { id: 'attendance', label: 'Attendance', icon: Clock },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  const handleTabChange = (tabId, subTab = null) => {
    // Handle attendance tab navigation first (before setting activeTab)
    if (tabId === 'attendance') {
      if (subTab) {
        // Set the specific sub-tab (e.g., 'approve_leave')
        setAttendanceInitialTab(subTab);
        // Force remount by updating key if already on attendance tab
        if (activeTab === 'attendance') {
          setAttendanceKey(prev => prev + 1);
        }
      } else if (activeTab === 'overview') {
        // Default to checkin when navigating from overview without subTab
        setAttendanceInitialTab('checkin');
      }
    } else if (tabId !== 'attendance') {
      // Reset to 'checkin' when navigating away from attendance
      setAttendanceInitialTab('checkin');
    }
    
    // If navigating to camp_submissions from overview (via count card), set initial tab to 'list'
    if (tabId === 'camp_submissions' && activeTab === 'overview') {
      setCampSubmissionsInitialTab('list');
    } else if (tabId !== 'camp_submissions') {
      // Reset to 'form' when navigating away
      setCampSubmissionsInitialTab('form');
    }
    
    // Set active tab last to ensure initialTab is set first
    setActiveTab(tabId);
  };

  return (
    <>
      <Helmet>
        <title>Admin Dashboard - {hospitalName}</title>
        <meta name="description" content={`Manage your dental practice at ${hospitalName} with comprehensive admin controls for doctors, patients, and system settings.`} />
      </Helmet>
      
      <div className="min-h-screen">
        {/* Header */}
        <header className="glass-effect border-b border-gray-700">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
            <div className="flex justify-between items-center h-14 sm:h-16">
              <div className="flex items-center space-x-1 sm:space-x-2">
                {branding?.logoUrl ? (
                  <img 
                    src={branding.logoUrl} 
                    alt={`${hospitalName} Logo`} 
                    className="h-8 sm:h-10 md:h-12 w-auto object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                ) : (
                  <>
                    <Stethoscope className="h-6 w-6 sm:h-8 sm:w-8 text-blue-400" />
                    <span className="text-base sm:text-xl font-bold gradient-text">{hospitalName}</span>
                  </>
                )}
              </div>
              
              <div className="flex items-center space-x-2 sm:space-x-4">
                {/* View as Admin checkbox - only for chief_doctor */}
                {isChiefDoctor && (
                  <label className="flex items-center space-x-1 sm:space-x-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={viewAsAdmin}
                      onChange={(e) => {
                        const newValue = e.target.checked;
                        setViewAsAdmin(newValue);
                        // Reset to overview tab when toggling
                        handleTabChange('overview');
                        // Refetch data when toggling
                        if (newValue && isChiefDoctor) {
                          fetchDoctors();
                          // When viewing as admin, fetch all patients (no filter)
                          fetchPatients(null, true);
                        } else {
                          // When not viewing as admin, fetch normally (tabs will handle filtering)
                          fetchPatients();
                        }
                        // Refetch other data that depends on viewAsAdmin
                        fetchAppointments();
                        fetchSubmissions();
                        fetchCampSubmissionsCount();
                        fetchAttendanceCount();
                        if (newValue && isChiefDoctor) {
                          fetchLeaveRequestsCount();
                        }
                      }}
                      className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 focus:ring-offset-gray-900 cursor-pointer"
                    />
                    <span className="text-white text-[10px] sm:text-xs md:text-sm group-hover:text-blue-400 transition-colors whitespace-nowrap">
                      <span className="hidden sm:inline">View as Admin</span>
                      <span className="sm:hidden">Admin</span>
                    </span>
                  </label>
                )}
                <div className="hidden sm:flex flex-col items-end">
                  <span className="text-white text-xs sm:text-sm">Welcome, {adminName}</span>
                  {roleDisplayName && (
                    <span className="text-gray-400 text-[10px] sm:text-xs mt-0.5">{roleDisplayName}</span>
                  )}
                  {profile?.doctor_unique_id && (
                    <span className="text-gray-400 text-[10px] sm:text-xs mt-0.5">ID: {profile.doctor_unique_id}</span>
                  )}
                </div>
                <div className="sm:hidden flex flex-col items-end">
                  <span className="text-white text-xs truncate max-w-[120px]">Welcome, {adminName}</span>
                  {roleDisplayName && (
                    <span className="text-gray-400 text-[10px] mt-0.5">{roleDisplayName}</span>
                  )}
                </div>
                <Button 
                  variant="ghost" 
                  onClick={handleLogout}
                  className="text-white hover:text-red-400 p-2 sm:px-3 sm:py-2"
                  size="sm"
                >
                  <LogOut className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Logout</span>
                </Button>
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
          {/* Welcome Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-4 sm:mb-6 md:mb-8"
          >
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold gradient-text mb-1 sm:mb-2">
                  Admin Dashboard
                </h1>
                <p className="text-gray-300 text-sm sm:text-base md:text-lg">
                  Manage your dental practice and system settings
                </p>
              </div>
              <div className="text-right">
                <div className="flex flex-col sm:flex-row gap-3 items-end">
                  {/* Plan Type and Expiry Date - Only show if plan is not TRIAL */}
                  {subscriptionInfo && subscriptionInfo.planType && subscriptionInfo.planType !== 'TRIAL' && (
                    <div className="p-3 sm:p-4 bg-gray-800/90 rounded-lg border border-green-500/50 shadow-lg min-w-[140px] sm:min-w-[180px]">
                      <p className="text-gray-400 text-xs mb-2">
                        Current Plan :<span className="text-white font-bold text-sm sm:text-base ml-1 uppercase">{subscriptionInfo.planType}</span>
                        {subscriptionInfo.paymentPlan && (
                          <span className="text-gray-400 text-xs ml-1 font-normal normal-case"> / {subscriptionInfo.paymentPlan}</span>
                        )}
                      </p>
                      {subscriptionInfo.expiryDate && (
                        <p className="text-gray-400 text-xs">
                          Expires On:<span className="text-green-400 font-semibold text-sm sm:text-base ml-1">
                            {subscriptionInfo.expiryDate.toLocaleDateString("en-IN", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                              timeZone: "Asia/Kolkata",
                            })}
                          </span>
                        </p>
                      )}
                    </div>
                  )}
                  
                  <div className="p-3 sm:p-4 bg-gray-800/90 rounded-lg border border-gray-700 shadow-lg min-w-[140px] sm:min-w-[180px]">
                    <p className="text-gray-400 text-xs mb-2">Current Date & Time (IST)</p>
                    <p className="text-white font-semibold text-sm sm:text-base mb-1">
                      {currentDateTime.toLocaleDateString("en-IN", {
                        weekday: "short",
                        year: "numeric",
                        month: "short",
                        day: "2-digit",
                        timeZone: "Asia/Kolkata",
                      })}
                    </p>
                    <p className="text-blue-400 font-mono text-lg sm:text-xl font-bold">
                      {currentDateTime.toLocaleTimeString("en-IN", {
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                        hour12: true,
                        timeZone: "Asia/Kolkata",
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Navigation Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-4 sm:mb-6 md:mb-8"
          >
            <div className="flex space-x-1 bg-gray-800 p-1 rounded-lg overflow-x-auto scrollbar-hide">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-md transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === tab.id
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  <tab.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="text-xs sm:text-sm">{tab.label}</span>
                </button>
              ))}
            </div>
          </motion.div>

          {/* Trial Period Active Section - Only for Clinic Admin or Chief Doctor with View as Admin */}
          {((userRole === 'CLINIC_ADMIN' || userRole === 'clinic_admin') || (isChiefDoctor && viewAsAdmin)) && planType === 'TRIAL' && trialInfo && trialInfo.isActive && trialInfo.daysLeft > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6"
            >
              <Card className="glass-effect border-yellow-500/50 bg-gradient-to-r from-yellow-500/10 to-orange-500/10">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <AlertCircle className="h-6 w-6 text-yellow-400 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-2">Trial Period Active</h3>
                      <p className="text-gray-300 mb-3">
                        Your trial period ends on <strong className="text-white">{trialInfo.endDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</strong>
                      </p>
                      <p className="text-yellow-400 font-semibold mb-4">
                        {trialInfo.daysLeft} {trialInfo.daysLeft === 1 ? 'day' : 'days'} left
                      </p>
                      <p className="text-gray-300 mb-4">
                        Please enroll before your trial period ends to continue using our services.
                      </p>
                      {hasPendingEnrollment && pendingEnrollmentRequest ? (
                        <div className="space-y-2">
                          <div className="p-3 bg-blue-500/20 border border-blue-500/50 rounded-lg">
                            <p className="text-blue-300 font-semibold mb-1">
                              Your plan enrollment is pending approval from Dental Hub Admin
                            </p>
                            <p className="text-gray-300 text-sm">
                              Plan: <strong className="text-white">{pendingEnrollmentRequest.plan_type}</strong> - <strong className="text-white">{pendingEnrollmentRequest.payment_plan}</strong>
                            </p>
                            <p className="text-gray-400 text-xs mt-1">
                              Submitted on: {new Date(pendingEnrollmentRequest.requested_at).toLocaleDateString('en-US', { 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                          <Button 
                            size="lg" 
                            disabled 
                            className="bg-gray-600 text-gray-400 cursor-not-allowed opacity-50"
                          >
                            Enroll Now
                            <ArrowRight className="ml-2 h-5 w-5" />
                          </Button>
                        </div>
                      ) : (
                        <Link to={`/pricing?hospital=${hospital?.slug || branding?.slug}`}>
                          <Button size="lg" className="bg-gradient-to-r from-yellow-500 to-orange-600 text-white hover:opacity-90">
                            Enroll Now
                            <ArrowRight className="ml-2 h-5 w-5" />
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Tab Content */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            {activeTab === 'overview' && <AdminOverviewTab stats={stats} submissions={submissions} patients={patients} appointments={appointments} doctors={doctors} appointmentsCount={appointmentsCount} scheduledAppointmentsCount={scheduledAppointmentsCount} campSubmissionsCount={campSubmissionsCount} attendanceCount={attendanceCount} leaveRequestsCount={leaveRequestsCount} leaveRequestsPendingCount={leaveRequestsPendingCount} leaveRequestsApprovedCount={leaveRequestsApprovedCount} onNavigate={handleTabChange} />}
            {activeTab === 'doctors' && <AdminDoctorsTab doctors={doctors} addDoctor={addDoctor} updateDoctor={updateDoctor} viewAsAdmin={viewAsAdmin && isChiefDoctor} />}
            {activeTab === 'patients' && <AdminPatientsTab onSubmissionComplete={refreshData} viewAsAdmin={viewAsAdmin && isChiefDoctor} />}
            {activeTab === 'appointments' && <AppointmentsTab viewAsAdmin={viewAsAdmin && isChiefDoctor} />}
            {activeTab === 'submissions' && <AdminSubmissionsTab submissions={submissions} />}
            {activeTab === 'camp_submissions' && <CampSubmissions initialTab={campSubmissionsInitialTab} viewAsAdmin={viewAsAdmin && isChiefDoctor} />}
            {activeTab === 'attendance' && <Attendance key={`attendance-${attendanceInitialTab}-${attendanceKey}`} initialTab={attendanceInitialTab} viewAsAdmin={viewAsAdmin && isChiefDoctor} isAdmin={viewAsAdmin && isChiefDoctor} />}
            {activeTab === 'settings' && <AdminSettingsTab />}
          </motion.div>
        </div>

        {/* Footer */}
        <footer className="border-t border-gray-700 py-8 px-4 sm:px-6 lg:px-8 mt-8">
          <div className="max-w-7xl mx-auto text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              {branding?.logoUrl ? (
                <img 
                  src={branding.logoUrl} 
                  alt={`${hospitalName} Logo`} 
                  className="h-8 w-auto object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const fallback = e.target.nextElementSibling;
                    if (fallback) {
                      fallback.style.display = 'flex';
                    }
                  }}
                />
              ) : (
                <>
                  <Stethoscope className="h-6 w-6 text-blue-400" />
                  <span className="text-lg font-semibold gradient-text">{hospitalName}</span>
                </>
              )}
            </div>
            <p className="text-gray-400 text-sm">
              ©2026 Dental Hub, Product of  <a 
                href="https://www.neinus.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline transition-colors"
              >
                Neinus Technologies
              </a>. All rights reserved.
              
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default AdminDashboard;